/**
 * CANLI MASA AKIŞI — GM'in oyuncuların zarlarını gördüğü ekran.
 *
 * Bu bileşen "B" seçeneğini uygular: atış anında jeton zıplayıp zarı
 * gösterir, sonra yerine oturur.
 *
 * TASARIM KARARLARI
 *
 * 1) POLLING, WEBSOCKET DEĞİL.
 *    Masada atış sıklığı dakikada 1-3. WebSocket kurmak Durable Object ve
 *    reconnect mantığı demek; polling'de ikisi de yok. Maliyet: 3 sn'de bir,
 *    istemci başına ~20 istek/dk. 4 saatlik seans ≈ 24.000 istek, Workers
 *    Free'in günlük 100.000 isteğine rahat sığar. Gecikme en kötü 3 sn ve
 *    masada fark edilmez.
 *
 * 2) TEKRAR EDEN ATIŞLAR `id` İLE ELENİR.
 *    Sunucu `created_at >= since` kullanır (bkz. packages/db/src/table.ts) ve
 *    imleç satırını bilinçli olarak tekrar döndürür. Burada `seen` kümesi o
 *    tekrarı söker. Aynı ms'ye düşen iki atışın KAYBOLMAMASI bu yüzden
 *    önemli: `>` kullanılsaydı bir zar hiç görünmezdi.
 *
 * 3) ÇEVRİMDIŞINDA SESSİZ.
 *    Zarhane PWA'dır; oyuncu uçak modunda da atış yapar. Yayın başarısız
 *    olursa atış YİNE de yerel overlay'de görünür, sadece GM görmez.
 *
 * 4) HARİTA = KATMANLAR.
 *    Arka planda harita görüntüsü, üstünde jetonlar. Görüntü yoksa
 *    ızgara çizilir — çünkü harita görüntüsü yüklemek zorunda değil ve
 *    oyun hemen oynanabilir olmalı.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

/** API'den gelen canlı atış (packages/db/src/table.ts ile aynı). */
export interface LiveRoll {
  id: string
  characterId: string | null
  characterName: string | null
  rollerId: string | null
  rollerName: string | null
  label: string | null
  formula: string
  seed: number | null
  total: number
  breakdown: { sides: number; value: number; kept: boolean }[]
  createdAt: number
}

export interface TableMember {
  id: string
  name: string
  level: number
  owner_id: string
  owner_name: string | null
}

interface Token {
  characterId: string
  x: number
  y: number
}

const POLL_MS = 3000
const ROLL_LIFETIME_MS = 9000

export interface TableViewProps {
  apiBase: string
  /** Harita görüntüsü (isteğe bağlı). Verilmezse ızgara çizilir. */
  mapImage?: string | null
  /** GM jetonları sürükleyebilir mi? */
  canMoveTokens?: boolean
}

export function TableView({ apiBase, mapImage, canMoveTokens = true }: TableViewProps) {
  const [members, setMembers] = useState<TableMember[]>([])
  const [gmName, setGmName] = useState<string | null>(null)
  const [tokens, setTokens] = useState<Record<string, Token>>({})
  const [log, setLog] = useState<LiveRoll[]>([])
  const [active, setActive] = useState<LiveRoll[]>([])
  const [offline, setOffline] = useState(false)

  // `seen` ve imleç refs: yeniden render tetiklemez, her sorguda güncellenir.
  const cursorRef = useRef(0)
  const seenRef = useRef<Set<string>>(new Set())

  const getJson = useCallback(
    async (path: string) => {
      const response = await fetch(`${apiBase}${path}`, { credentials: 'include' })
      if (!response.ok) throw new Error(`${path} -> ${response.status}`)
      return response.json()
    },
    [apiBase],
  )

  // --- roster ve jetonlar (bir kez) ---
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const [chars, tokenData] = await Promise.all([
          getJson('/api/table/characters'),
          getJson('/api/table/tokens'),
        ])
        if (cancelled) return
        setMembers(chars.characters ?? [])
        // GM adı oyunculara da görünür: masanın yöneticisi kim, belli olsun.
        setGmName(chars.gmName ?? null)
        const map: Record<string, Token> = {}
        for (const token of tokenData.tokens ?? []) map[token.characterId] = token
        setTokens(map)
      } catch {
        if (!cancelled) setOffline(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [getJson])

  // --- canlı akış ---
  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined

    const tick = async () => {
      try {
        const data = await getJson(`/api/table/rolls?since=${cursorRef.current}`)
        if (cancelled) return
        setOffline(false)

        // Sunucu en yeniden gönderir; istemci eskiden yeniye çevirir.
        const fresh = (data.rolls as LiveRoll[])
          .slice()
          .reverse()
          .filter((roll) => !seenRef.current.has(roll.id))

        if (fresh.length > 0) {
          for (const roll of fresh) seenRef.current.add(roll.id)
          cursorRef.current = data.serverTime ?? fresh[fresh.length - 1]!.createdAt
          setLog((prev) => [...fresh.reverse(), ...prev].slice(0, 40))
          setActive(fresh)
        } else if ((data.rolls as LiveRoll[]).length > 0) {
          // Yeni atis yoksa imlec yine ilerlemeli, yoksa hep ayni satir gelir.
          cursorRef.current = data.serverTime ?? cursorRef.current
        }
      } catch {
        if (!cancelled) setOffline(true)
      } finally {
        if (!cancelled) timer = setTimeout(tick, POLL_MS)
      }
    }

    timer = setTimeout(tick, POLL_MS)
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [getJson])

  // --- atış görseli süresi ---
  useEffect(() => {
    if (active.length === 0) return
    const timer = setTimeout(() => setActive([]), ROLL_LIFETIME_MS)
    return () => clearTimeout(timer)
  }, [active])

  // --- jeton taşıma ---
  const move = useCallback(
    (characterId: string, xPercent: number, yPercent: number) => {
      setTokens((prev) => ({ ...prev, [characterId]: { characterId, x: xPercent, y: yPercent } }))
      // Sunucu yazmasi hata verirse konum eskiye donmeli; kullanici
      // jetonun yerinde durdugunu sanmasin.
      fetch(`${apiBase}/api/table/token`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ characterId, x: xPercent, y: yPercent }),
      }).catch(() => {
        void getJson('/api/table/tokens').then((data) => {
          const map: Record<string, Token> = {}
          for (const token of data.tokens ?? []) map[token.characterId] = token
          setTokens(map)
        })
      })
    },
    [apiBase, getJson],
  )

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          {/* GM adı OYUNCULARA da görünür: masanın yöneticisi kim, belli olsun.
              Rol tabanlıdır — sahip (owner) olan kullanıcıdır, isim serbest
              metindir ("Boşluğun Hükümdarı" gibi bir unvan olabilir). */}
          <h2 className="font-display text-lg font-semibold text-ink">
            {gmName ? `${gmName}'in Masası` : 'Masa'}
          </h2>
          {members.length > 0 && (
            <p className="text-[0.65rem] text-ink-faint">{members.length} karakter masada</p>
          )}
        </div>
        <span className={`text-xs ${offline ? 'text-blood' : 'text-ink-faint'}`} aria-live="polite">
          {offline ? 'bağlantı yok — atışlar kaydediliyor, yayınlanmıyor' : 'canlı'}
        </span>
      </div>

      {/* --- HARİTA --- */}
      <div className="relative aspect-[4/3] w-full overflow-hidden rounded border border-line-soft bg-void select-none">
        {mapImage ? (
          <img src={mapImage} alt="" className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          // Harita görüntüsü yoksa ızgara: oyun hemen oynanabilir olsun.
          <div
            aria-hidden="true"
            className="absolute inset-0 opacity-25"
            style={{
              backgroundImage:
                'linear-gradient(to right, #2a3f4f 1px, transparent 1px), linear-gradient(to bottom, #2a3f4f 1px, transparent 1px)',
              backgroundSize: '10% 10%',
            }}
          />
        )}

        {/* JETONLAR + ANLIK ZAR GÖSTERGESİ */}
        {members.map((member) => {
          const token = tokens[member.id] ?? { characterId: member.id, x: 50, y: 50 }
          // Bu karakterin ANLIK atışı: varsa jetonun üstünde gösterilir.
          const pop = active.find((roll) => roll.characterId === member.id)
          return (
            <div
              key={member.id}
              className="absolute"
              style={{ left: `${token.x}%`, top: `${token.y}%` }}
            >
              {pop && (
                <div
                  className="absolute bottom-full left-1/2 z-20 mb-1 -translate-x-1/2 whitespace-nowrap rounded border border-gold/50 bg-void/95 px-2 py-1 text-center shadow-lg animate-bounce-once"
                  role="status"
                >
                  <div className="text-[0.6rem] tracking-widest text-ink-faint uppercase">
                    {pop.label ?? pop.formula}
                  </div>
                  <div className="tnum font-display text-xl font-bold text-gold">{pop.total}</div>
                  <div className="text-[0.6rem] text-ink-faint">
                    {pop.breakdown.map((d) => `d${d.sides}:${d.value}`).join(' + ')}
                  </div>
                </div>
              )}
              <button
                type="button"
                disabled={!canMoveTokens}
                className={`relative z-10 -translate-x-1/2 -translate-y-1/2 rounded-full border px-2 py-1 text-[0.65rem] transition-colors ${
                  pop
                    ? 'animate-pop border-gold bg-gold/25 text-gold'
                    : 'border-line-soft bg-surface-2 text-ink hover:border-gold/50'
                } ${canMoveTokens ? 'cursor-grab active:cursor-grabbing' : ''}`}
                style={{ transform: 'translate(-50%, -50%)' }}
                onPointerDown={(event) => {
                  if (!canMoveTokens) return
                  const host = event.currentTarget.parentElement?.parentElement
                  if (!host) return
                  const moveTo = (clientX: number, clientY: number) => {
                    const rect = host.getBoundingClientRect()
                    const x = ((clientX - rect.left) / rect.width) * 100
                    const y = ((clientY - rect.top) / rect.height) * 100
                    move(member.id, Math.max(0, Math.min(100, x)), Math.max(0, Math.min(100, y)))
                  }
                  const onMove = (e: PointerEvent) => moveTo(e.clientX, e.clientY)
                  const onUp = () => {
                    window.removeEventListener('pointermove', onMove)
                    window.removeEventListener('pointerup', onUp)
                  }
                  window.addEventListener('pointermove', onMove)
                  window.addEventListener('pointerup', onUp)
                }}
                title={canMoveTokens ? 'Sürükle' : undefined}
              >
                {member.name}
                {pop && <span className="ml-1 font-bold">{pop.total}</span>}
              </button>
            </div>
          )
        })}
      </div>

      {/* --- ATIŞ GÜNLÜĞÜ --- */}
      <div>
        <h3 className="mb-1 text-[0.6rem] tracking-[0.16em] text-ink-faint uppercase">Atışlar</h3>
        {log.length === 0 ? (
          <p className="text-xs text-ink-faint">
            Henüz atış yok. Oyuncular sayılara tıklayınca burada belirir.
          </p>
        ) : (
          <ol className="space-y-1">
            {log.map((roll) => (
              <li
                key={roll.id}
                className="flex items-baseline gap-2 rounded border border-line-soft bg-surface-2 px-2 py-1 text-xs"
              >
                <span className="shrink-0 text-ink-faint">
                  {roll.characterName ?? roll.rollerName ?? '—'}
                </span>
                <span className="text-ink">{roll.label ?? roll.formula}</span>
                <span className="tnum ml-auto font-semibold text-gold">{roll.total}</span>
                <span className="tnum shrink-0 text-ink-faint">
                  {roll.breakdown.map((d) => `d${d.sides}:${d.value}`).join('+')}
                </span>
                {roll.seed != null && (
                  <span className="tnum shrink-0 text-[0.6rem] text-ink-faint" title="tohum">
                    #{roll.seed}
                  </span>
                )}
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  )
}
